<template>
    <header
        class="header main-site"
        :class="{
            [`theme-${layout.theme}`]: true,
        }"
    >
        <div
            v-if="isDesktop"
            class="content desktop"
        >
            <div class="left">
                <nuxt-link class="logo" :to="resolveLogoLink">
                    <img v-bind="logo" >
                </nuxt-link>
            </div>
            <div v-if="$slots.center" class="center">
                <slot name="center" />
            </div>
            <div v-if="$slots.right" class="right">
                <slot name="right" />
            </div>
            <div v-if="$slots.bottom" class="bottom">
                <slot name="bottom" />
            </div>
        </div>
        <template v-else>
            <div class="left">
                <nuxt-link class="logo" :to="resolveLogoLink">
                    <img v-bind="logo" >
                </nuxt-link>
            </div>
            <button class="mobile-nav" @click="layout.sidebars.right = true">
                <DSIcon
                    code="menu"
                    size="xxl"
                />
            </button>
        </template>
    </header>
</template>
<script setup>
import { ref, computed, inject, watch, onMounted, onUnmounted, storeToRefs, useNuxtApp, useRoute } from '#imports';
import { usePageStore } from '~dsc/stores/cms/page';
import { useDebounceFn } from '@vueuse/core';
const { $bp, $paths } = useNuxtApp();
const { pageData } = storeToRefs(usePageStore());
const route = useRoute();

const layout = inject('layout');
const scrollY = ref(0);
const firstBlockIsHeroFullscreen = computed(() => pageData.value?.[0]?.display?.type === 'fullscreen');
const isDesktop = computed(() => $bp.bps.isDesktop);
const logo = { src: '/images/logo.svg', alt: 'St1' };

const resolveLogoLink = computed(() => {
    const path = route.path?.replace(/\//, '')?.split('/') ?? [];
    const b2b = $paths.getPath('b2b');

    if (b2b.replace(/\//, '') === path?.[0]) {
        return b2b;
    }

    return $paths.getPath('landingPage');
});

defineOptions({
    inheritAttrs: false,
});

const updateScroll = useDebounceFn(() => {
    scrollY.value = window?.scrollY ?? 0;
}, 50);

defineProps({
    navigation: { type: Array, default: () => [] },
});

onMounted(() => {
    updateScroll();
    document.addEventListener('scroll', updateScroll, { passive: true });
});

onUnmounted(() => {
    document.removeEventListener('scroll', updateScroll, { passive: true });
});

watch([scrollY, firstBlockIsHeroFullscreen], () => {
    if (scrollY.value >= 50 || !firstBlockIsHeroFullscreen.value) {
        layout.value.theme = 'white';
    } else if (firstBlockIsHeroFullscreen.value && scrollY.value < 50) {
        layout.value.theme = 'clear';
    }
});
</script>
<style scoped lang="scss">
@import '../../assets/scss/mixins';

.header {
    width: 100%;
    position: fixed;

    .mobile-nav {
        display: inline-flex;
        justify-self: end;
        -webkit-tap-highlight-color: transparent;
    }

    &.theme-clear {
        --ds-icon-color: var(--color-base-white);
    }

    .icon.icon-hamburger-two-row {
        @include default-transition();
    }

    .content {
        display: grid;
        width: 100%;
        max-width: var(--desktop-max-width);
        margin: 0 auto;
        padding: 1.25rem 1.5rem;
        grid-template-columns: auto 1fr auto;
        gap: 0 1.5rem;
    }

    .center {
        display: inline-flex;
        align-items: center;
        overflow-x: hidden;
    }

    .right {
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-items: flex-end;
        justify-self: end;

        > *:not(:first-child) {
            margin-left: .5rem;
        }
    }

    .bottom {
        grid-column-end: span 3;
        max-width: 100%;
        overflow: hidden;

        &:not(:empty) {
            margin-top: 1rem;
        }
    }
}

.logo {
    display: inline-flex;

    .svg-container, .image, img {
        height: 42px;
        image-rendering: smooth;
    }
}

@include respond-to('at most small desktop') {
    .header {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: 3rem;
        padding: .5rem 1.5rem;
        align-items: center;
    }
}
</style>
